import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useConsentStore, FEATURE_CONSENT_KEYS } from "@/store/consentStore";
import { useAuthStore } from "@/store/authStore";
import { consentApi } from "@/lib/api";

const LABELS = {
  healthDataCollection: "Allow processing of my health records & reports",
  aiAnalysis: "Allow AI analysis of my reports (Dr Sanjeevani)",
  emergencyDataSharing: "Allow sharing my data with responders during emergencies",
};

export default function FeatureConsentGate() {
  const gateVisible = useConsentStore((s) => s.gateVisible);
  const resolveGate = useConsentStore((s) => s.resolveGate);
  const user = useAuthStore((s) => s.user);
  const [checked, setChecked] = useState({});
  const [saving, setSaving] = useState(false);

  const allChecked = FEATURE_CONSENT_KEYS.every((k) => checked[k]);

  const handleAgree = async () => {
    if (!allChecked || !user?.phone) return;
    setSaving(true);
    try {
      await consentApi.recordBulk({
        phone: user.phone,
        userId: user._id,
        consentVersion: "2.0",
        consents: FEATURE_CONSENT_KEYS.map((k) => ({ consentType: k, consented: true })),
      });
      resolveGate(true);
      setChecked({});
      toast.success("Consent saved");
    } catch (e) {
      toast.error("Could not save consent. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={gateVisible} onOpenChange={(o) => !o && resolveGate(false)}>
      <DialogContent className="sm:max-w-md rounded-3xl">
        <DialogHeader>
          <div className="h-12 w-12 rounded-2xl bg-secondary flex items-center justify-center mb-2">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle>A quick consent to continue</DialogTitle>
          <DialogDescription>
            To use Health Records, Trata AI and emergency features, we need your explicit consent
            under the India DPDP Act.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {FEATURE_CONSENT_KEYS.map((k) => (
            <label
              key={k}
              className="flex items-start gap-3 p-3 rounded-2xl border border-border cursor-pointer hover:bg-accent"
            >
              <Checkbox
                checked={!!checked[k]}
                onCheckedChange={(v) => setChecked((c) => ({ ...c, [k]: !!v }))}
                data-testid={`consent-${k}`}
                className="mt-0.5"
              />
              <span className="text-sm text-foreground">{LABELS[k]}</span>
            </label>
          ))}
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1 rounded-2xl"
            onClick={() => resolveGate(false)}
            data-testid="consent-decline"
          >
            Not now
          </Button>
          <Button
            className="flex-1 rounded-2xl"
            disabled={!allChecked || saving}
            onClick={handleAgree}
            data-testid="consent-agree"
          >
            {saving ? "Saving..." : "Agree & Continue"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
